/** Position progression — SVG bump chart showing driver positions across sessions.
 *
 *  Uses the lightweight GetCategoryProgression endpoint which returns just
 *  position data for all sessions in a single call (no lap data).
 *
 *  Layout:
 *    [Pos column]  [SVG bump chart spanning all rows]  [Driver column]
 *
 *  Each driver's position in a session is plotted at the Y-coordinate of that
 *  position's row, so lines cross between rows to visualise position changes.
 *
 *  All lines are grey by default. Hovering a line, dot, or driver name
 *  highlights the full driver path in purple. Clicking locks the
 *  highlight until clicking away.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { rpc } from '@/api/client';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { GetCategoryProgressionResponse, CategoryProgressionDriver } from '@/types/telemetry';

interface PositionProgressionProps {
  eventId: string;
  categoryName: string;
  highlightDriverId?: string;
}

const ROW_HEIGHT = 36;
const COLOR_DEFAULT = '#6b7280';     // grey-500
const COLOR_DIMMED = '#374151';      // grey-700 (faded when another is highlighted)
const COLOR_HIGHLIGHT = '#7c5cfc';   // accent purple

const GROUPED_HEAT_TYPES = ['heat-ab', 'heat-bc', 'heat-ac'];

export function PositionProgression({ eventId, categoryName, highlightDriverId }: PositionProgressionProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<GetCategoryProgressionResponse | null>(null);
  const [hoveredDriverId, setHoveredDriverId] = useState<string | null>(null);
  const [lockedDriverId, setLockedDriverId] = useState<string | null>(null);

  // Effective highlighted driver: prop > locked > hover
  const activeDriverId = highlightDriverId ?? lockedDriverId ?? hoveredDriverId;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await rpc<GetCategoryProgressionResponse>('GetCategoryProgression', {
        event_id: eventId,
        category_name: categoryName,
      });
      setData(res);
    } catch {
      // Silently fail
    } finally {
      setLoading(false);
    }
  }, [eventId, categoryName]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Determine if grouped heats are present → filter to qualifying + final only
  const hasGroupedHeats = useMemo(
    () => data?.sessionTypes?.some(st => GROUPED_HEAT_TYPES.includes(st)) ?? false,
    [data],
  );

  const sessionTypes = useMemo(() => {
    if (!data?.sessionTypes) return [];
    if (hasGroupedHeats) {
      return data.sessionTypes.filter(st => st === 'qualifying' || st === 'final');
    }
    return data.sessionTypes;
  }, [data, hasGroupedHeats]);

  // Build driver list sorted by final position, DNF/DNS at bottom
  const drivers = useMemo(() => {
    if (!data?.drivers) return [];
    return [...data.drivers].sort((a, b) => {
      const aFinal = a.positions?.final ?? 0;
      const bFinal = b.positions?.final ?? 0;
      const aPos = aFinal > 0 ? aFinal : 9999;
      const bPos = bFinal > 0 ? bFinal : 9999;
      if (aPos !== bPos) return aPos - bPos;
      const aQuali = a.positions?.qualifying ?? 9999;
      const bQuali = b.positions?.qualifying ?? 9999;
      return aQuali - bQuali;
    });
  }, [data]);

  // Total rows = max position across all sessions
  const totalRows = useMemo(() => {
    let max = drivers.length;
    for (const d of drivers) {
      if (d.positions) {
        for (const st of sessionTypes) {
          const pos = d.positions[st];
          if (pos && pos > max) max = pos;
        }
      }
    }
    return max;
  }, [drivers, sessionTypes]);

  if (loading) return <LoadingSpinner />;
  if (drivers.length === 0 || sessionTypes.length === 0) {
    return <p className="text-sm text-text-muted text-center py-12">{t('races.noData')}</p>;
  }

  const cols = sessionTypes.length;
  const svgHeight = totalRows * ROW_HEIGHT;

  // Map a 1-based position to a Y coordinate (centre of that row)
  const posToY = (pos: number) => (pos - 1) * ROW_HEIGHT + ROW_HEIGHT / 2;

  /** Resolve colour and opacity for a driver given the current highlight state. */
  const getDriverStyle = (driverId: string) => {
    if (!activeDriverId) {
      return { color: COLOR_DEFAULT, opacity: 0.6 };
    }
    if (driverId === activeDriverId) {
      return { color: COLOR_HIGHLIGHT, opacity: 1 };
    }
    return { color: COLOR_DIMMED, opacity: 0.2 };
  };

  const handleDriverHover = (driverId: string | null) => {
    if (!highlightDriverId) setHoveredDriverId(driverId);
  };

  /** Click a driver element → toggle lock. */
  const handleDriverClick = (e: React.MouseEvent, driverId: string) => {
    e.stopPropagation(); // prevent click-away handler
    if (highlightDriverId) return;
    setLockedDriverId(prev => (prev === driverId ? null : driverId));
  };

  /** Click the card background → clear lock. */
  const handleClickAway = () => {
    if (!highlightDriverId) setLockedDriverId(null);
  };

  return (
    <div className="card p-0 overflow-hidden" onClick={handleClickAway}>
      <div className="overflow-x-auto -mx-5 sm:-mx-0">
        <div
          className="flex"
          style={{ minWidth: 520 }}
          onMouseLeave={() => handleDriverHover(null)}
        >
          {/* Chart + rows area */}
          <div className="flex-1 min-w-0">
            {/* Header for session types */}
            <div className="flex text-text-muted text-[11px] uppercase tracking-wider border-b border-border-subtle font-semibold">
              {sessionTypes.map(type => {
                const short: Record<string, string> = {
                  'qualifying': 'Q',
                  'heat-1': '1',
                  'heat-2': '2',
                  'heat-3': '3',
                  'final': 'F',
                };
                return (
                  <div key={type} className="flex-1 py-2.5 text-center">
                    <span className="sm:hidden">{short[type] ?? t(`races.sessionType.${type}` as any)}</span>
                    <span className="hidden sm:inline">{t(`races.sessionType.${type}` as any)}</span>
                  </div>
                );
              })}
            </div>

            {/* SVG bump chart */}
            <div className="relative">
              {/* Row grid lines */}
              {Array.from({ length: totalRows }, (_, i) => (
                <div
                  key={i}
                  className="border-b border-border-subtle/50"
                  style={{ height: ROW_HEIGHT }}
                />
              ))}

              {/* SVG overlay */}
              <svg
                className="absolute inset-0 w-full"
                style={{ height: svgHeight }}
              >
                {/* Lines layer (rendered first so dots appear on top) */}
                {drivers.map((driver) => {
                  const { color, opacity } = getDriverStyle(driver.driverId);

                  const points: { col: number; pos: number }[] = [];
                  sessionTypes.forEach((type, colIdx) => {
                    const pos = driver.positions?.[type];
                    if (pos && pos > 0) {
                      points.push({ col: colIdx, pos });
                    }
                  });

                  if (points.length < 2) return null;

                  // Draw line segments between consecutive points
                  return points.slice(1).map((p, i) => {
                    const prev = points[i];
                    const x1 = `${(prev.col + 0.5) / cols * 100}%`;
                    const y1 = posToY(prev.pos);
                    const x2 = `${(p.col + 0.5) / cols * 100}%`;
                    const y2 = posToY(p.pos);
                    return (
                      <line
                        key={`${driver.driverId}-line-${i}`}
                        x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke={color}
                        strokeWidth={driver.driverId === activeDriverId ? 3 : 2}
                        strokeLinecap="round"
                        opacity={opacity}
                        onMouseEnter={() => handleDriverHover(driver.driverId)}
                        onClick={(e) => handleDriverClick(e, driver.driverId)}
                        style={{ cursor: 'pointer' }}
                      />
                    );
                  });
                })}

                {/* Dots layer (on top of lines) */}
                {drivers.map((driver) => {
                  const { color, opacity } = getDriverStyle(driver.driverId);
                  const isActive = driver.driverId === activeDriverId;

                  const points: { col: number; pos: number }[] = [];
                  sessionTypes.forEach((type, colIdx) => {
                    const pos = driver.positions?.[type];
                    if (pos && pos > 0) {
                      points.push({ col: colIdx, pos });
                    }
                  });

                  if (points.length === 0) return null;

                  return points.map((p) => {
                    const cx = `${(p.col + 0.5) / cols * 100}%`;
                    const cy = posToY(p.pos);
                    const r = isActive ? 14 : 12;
                    return (
                      <g
                        key={`${driver.driverId}-dot-${p.col}`}
                        opacity={opacity}
                        onMouseEnter={() => handleDriverHover(driver.driverId)}
                        onClick={(e) => handleDriverClick(e, driver.driverId)}
                        style={{ cursor: 'pointer' }}
                      >
                        <circle
                          cx={cx}
                          cy={cy}
                          r={r}
                          fill={color}
                        />
                        <text
                          x={cx}
                          y={cy}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fill="white"
                          fontSize={isActive ? 11 : 10}
                          fontWeight={600}
                          style={{ pointerEvents: 'none' }}
                        >
                          {p.pos}
                        </text>
                      </g>
                    );
                  });
                })}
              </svg>
            </div>
          </div>

          {/* Right columns: kart number + driver name */}
          <div className="w-14 flex-shrink-0">
            <div className="py-2.5 px-3 text-center text-text-muted text-[11px] uppercase tracking-wider border-b border-border-subtle font-semibold">
              {t('races.kart')}
            </div>
            {Array.from({ length: totalRows }, (_, i) => {
              const pos = i + 1;
              const driver = drivers.find(d => (d.positions?.final ?? 0) === pos);
              const isActive = driver && activeDriverId === driver.driverId;
              return (
                <div
                  key={pos}
                  className={`flex items-center justify-center px-3 border-b border-border-subtle/50 transition-colors ${isActive ? 'bg-purple/10' : ''}`}
                  style={{ height: ROW_HEIGHT }}
                  onMouseEnter={() => driver && handleDriverHover(driver.driverId)}
                  onClick={(e) => driver && handleDriverClick(e, driver.driverId)}
                >
                  {driver ? (
                    <span className="mono text-text-secondary text-xs font-medium">{driver.kartNumber}</span>
                  ) : (
                    <span className="text-xs text-text-muted">–</span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="w-36 flex-shrink-0">
            <div className="py-2.5 px-3 text-text-muted text-[11px] uppercase tracking-wider border-b border-border-subtle font-semibold">
              {t('races.driver')}
            </div>
            {Array.from({ length: totalRows }, (_, i) => {
              const pos = i + 1;
              const driver = drivers.find(d => (d.positions?.final ?? 0) === pos);
              const isActive = driver && activeDriverId === driver.driverId;
              return (
                <div
                  key={pos}
                  className={`flex items-center px-3 border-b border-border-subtle/50 transition-colors ${isActive ? 'bg-purple/10' : ''}`}
                  style={{ height: ROW_HEIGHT }}
                  onMouseEnter={() => driver && handleDriverHover(driver.driverId)}
                  onClick={(e) => driver && handleDriverClick(e, driver.driverId)}
                >
                  {driver ? (
                    <button
                      className={`font-medium text-xs text-left truncate transition-colors ${isActive ? 'text-accent' : 'text-text-primary hover:text-accent'}`}
                      onClick={() => navigate(`#/races/driver/${driver.driverId}`)}
                    >
                      {driver.firstName} {driver.lastName}
                    </button>
                  ) : (
                    <span className="text-xs text-text-muted">–</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
